import {AfterViewInit, ChangeDetectorRef, Component, OnDestroy, QueryList, ViewChild, ViewChildren} from '@angular/core';
import {Proposal} from '../../model/proposal/proposal';
import {Route} from '../../enum/route';
import {TranslateService} from '@ngx-translate/core';
import {ActivatedRoute, Router} from '@angular/router';
import {BehaviorSubject, combineLatest, first, map, Observable} from 'rxjs';
import {RiskDataFormComponent} from '../../components/create-proposal/risk-data-form/risk-data-form.component';
import {ProposalFormBase} from '../../components/create-proposal/proposal-form-base/proposal-form-base.component';
import {DialogTwoOptionsComponent} from '../../components/dialogs/two-options-dialog/dialog-two-options.component';
import {UploadComponent} from '../../components/upload-field/upload.component';
import {Store} from '@ngrx/store';
import {ProposalAppState} from '../../store/proposal/reducers/proposal.reducer';
import {
  selectDetailPageProposal,
  selectDirtyFormPreview,
  selectMaxScrolledPreview,
  selectProposalPreview,
  selectProposalTransmission
} from '../../store/proposal/selectors/proposal.selectors';
import {FileUploadService} from '../../services/file-upload-service/file-upload.service';
import {ProposalDetailPageActions, ProposalTransmissionActions} from '../../store/proposal/actions/proposal.actions';
import {PreviewPageTab} from '../../enum/preview-page-tabs';
import {ProposalTile} from '../../model/proposal-tile';
import {UserAppState} from '../../store/user/reducers/user.reducer';
import {User} from '../../model/user';
import {selectUser} from '../../store/user/selectors/user.selectors';
import {MatDialog, MatDialogConfig} from '@angular/material/dialog';
import {Roles} from '../../enum/roles';

@Component({
    selector: 'app-create-proposal',
    templateUrl: './create-proposal.component.html',
    styleUrls: ['../../../styles/create-something.scss', '../../../styles/standard-elements.scss', './create-proposal.component.scss'],
    standalone: false
})
export class CreateProposalComponent implements AfterViewInit, OnDestroy {

  expandMore: boolean = true;
  idNames = [];
  scrolled = 0;
  maxScrolled = 0;
  scrollPosition = [0];
  proposal = new Proposal();
  proposalTile = new ProposalTile();
  allFormsValid$: Observable<boolean>;
  allUploadsComplete$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  validChildForm: Observable<boolean>[] = [];
  showMissingInformation$: Observable<boolean>;
  saveProposalButtonDisabled: boolean = false;
  activeUser: User;
  proposalPreview = this.proposalStore.select(selectProposalPreview);
  cancelClicked: boolean = false;
  maxScrolledPreview = 0;
  isFormDirty: boolean = false;
  showLobModal: boolean = false;

  isEditRoute: boolean = false;
  hasRiskLocationInput: boolean = false;

  @ViewChild(RiskDataFormComponent, {static: true}) riskDataComponent: RiskDataFormComponent;
  @ViewChildren(ProposalFormBase) formComponents: QueryList<ProposalFormBase>;
  @ViewChildren(UploadComponent) uploadComponents: QueryList<UploadComponent>;

  titles = [
    'partnerInformationQuestion',
    'riskDataQuestion',
    'sumsInsured',
    'riskDescriptionQuestion',
    'riskLocationQuestion',
    'internationalProgramQuestion',
    'previousDamageQuestion',
    'individualClausesConditionsQuestion',
    'adInformation',
    'dataUploadSummary'];

  constructor(private readonly translate: TranslateService,
              private readonly dialog: MatDialog,
              private readonly router: Router,
              private readonly changeDetector: ChangeDetectorRef,
              public proposalStore: Store<ProposalAppState>,
              private readonly userStore: Store<UserAppState>,
              private readonly activatedRoute: ActivatedRoute,
              public fileUploadService: FileUploadService) {
  }

  ngOnInit() {

    this.userStore.select(selectUser).subscribe(user => {
      if (user.offeringLOBs.length === 0) {
        this.showLobModal = true;
      }
    });

    let transmissionId: string = this.activatedRoute.snapshot.queryParamMap.get('id');
    let secret: string = this.activatedRoute.snapshot.queryParamMap.get('secret');
    let isValidTransmission: boolean = transmissionId !== null && secret !== null;

    if (isValidTransmission) {
      this.proposalStore.dispatch(ProposalTransmissionActions.load({id: transmissionId, secret}));
    }
    for (let i = 0; i < this.titles.length; i++) {
      this.idNames.push('question-block' + i);
    }

    this.fileUploadService.fileUploadStatus$.subscribe(fileUploadStatus => {
      if (fileUploadStatus) {
        this.allUploadsComplete$.next(fileUploadStatus.filter(fus => fus.progress != 100).length == 0);
      }
    });
    this.userStore.select(selectUser).subscribe(user => this.activeUser = user);
    this.proposalStore.select(selectMaxScrolledPreview).subscribe(maxScrolled => this.maxScrolledPreview = maxScrolled);
    this.proposalStore.select(selectDirtyFormPreview).subscribe(dirty => this.isFormDirty = dirty);

    let proposalId: string = this.activatedRoute.snapshot.paramMap.get('id');
    this.isEditRoute = proposalId != null;
    if(this.isEditRoute) {
      this.proposalStore.dispatch(
        ProposalDetailPageActions.load({ proposalId: proposalId })
      );
    }
  }


  ngOnDestroy() {
    if (!this.cancelClicked) {
      this.proposal = new Proposal();
      for (let form of this.formComponents) {
        form.updateProposal(this.proposal);
        if (form.formGroup?.dirty) {
          this.isFormDirty = true;
        }
      }
      this.mapProposalToProposalTile();
      this.proposalStore.dispatch(ProposalDetailPageActions.proposalPreview({ proposalPreview: this.proposal, proposalTilePreview: this.proposalTile, maxScrolled: this.maxScrolled, isFormDirty: this.isFormDirty }));
    }
    this.fileUploadService.clearAll();
  }

  scroll(id) {
    document.getElementById(id).scrollIntoView();
  }

  onWindowScroll(el: HTMLElement) {
    this.scrollPosition = [0];
    const smallestSectionHeight = 60; //for now smallest section height is 198px
    let scrollTop = el.scrollTop + smallestSectionHeight;
    for (let childrenKey in el.children) {
      this.scrollPosition.push(el.children[childrenKey].scrollHeight + this.scrollPosition[this.scrollPosition.length - 1]);
    }
    this.scrolled = 0;
    for (let i = 1; i < this.scrollPosition.length; i++) {
      if (scrollTop > this.scrollPosition[i] && this.titles[i]) {
        this.scrolled = i;
      }
    }
    for (let i = 0; i < this.scrolled; i++) {
      this.formComponents.get(i).formGroup.markAllAsTouched();
      this.formComponents.get(i).formGroup.updateValueAndValidity();
    }
    this.maxScrolled = Math.max(this.maxScrolled, this.scrolled);
    this.scroll(this.idNames[this.scrolled] + 'button');
  }

  cancel() {
    this.proposalStore.dispatch(ProposalDetailPageActions.resetPreview());
    this.proposal = new Proposal();
    for (let form of this.formComponents) {
      form.reset();
    }
    this.cancelClicked = true;
    this.router.navigate(['/' + Route.INSERATE]);
  }

  ngAfterViewInit(): void {
    const obs: Observable<boolean>[] = [];

    let transmissionId: string = this.activatedRoute.snapshot.queryParamMap.get('id');
    let secret: string = this.activatedRoute.snapshot.queryParamMap.get('secret');
    let isValidTransmission: boolean = transmissionId !== null && secret !== null;

    if (isValidTransmission) {
      this.proposalStore.select(selectProposalTransmission).subscribe(proposal => {
        if (proposal != null) {
          this.formComponents.forEach(component => {
            component.autoFillForTransmission(proposal);
          });
        }
      });
    }
    if(this.isEditRoute) {
      this.proposalStore.select(selectDetailPageProposal)
        .pipe(
          first(proposal => !!proposal)
        ).subscribe(proposal => {
        if (proposal != null) {
          this.formComponents.forEach(component => {
            component.autoFillForEdit(proposal);
          });
        }
      });
    }

    for (let formbase of this.formComponents) {
      obs.push(formbase.validStatus());
      this.validChildForm.push(formbase.validStatus());
    }
    this.maxScrolled = Math.max(this.maxScrolled, this.scrolled);

    this.showMissingInformation$ = combineLatest(obs)
      .pipe(
        map((array) => {
          this.maxScrolled = Math.max(this.maxScrolled, this.scrolled);

          return array.some((value, index) => {
            if (this.maxScrolled === 0) {
              return false;
            }
            return (!value && this.maxScrolled > index);
          });
        }));

    this.allFormsValid$ = combineLatest(obs)
      .pipe(
        map((array: boolean[]) => array.every(value => value)),
      );
    this.changeDetector.detectChanges();

    this.proposalPreview.subscribe(proposalPreview => {
      if (proposalPreview != null) {
        this.proposal = proposalPreview;
        //cycle added to update the validity of all the form components
        for (let i = 0; i < this.formComponents.length; i++) {
          this.formComponents.get(i).formGroup.markAllAsTouched();
          this.formComponents.get(i).formGroup.updateValueAndValidity();
        }
      }
    });

    if (this.activatedRoute.snapshot.queryParamMap.get('previousRoute') != 'previewPage' && this.isFormDirty) {
      this.openResetDialog();
    } else if (this.isFormDirty){
      this.scroll(this.idNames[9]);
    }
  }

  expand() {
    this.expandMore = !this.expandMore;
  }

 navigateToPreview(): void {
   const route = this.isEditRoute
     ? ['/', Route.INSERAT_ERSTELLEN, Route.PROPOSAL_PREVIEW, this.activatedRoute.snapshot.paramMap.get('id')]
     : ['/', Route.INSERAT_ERSTELLEN, Route.PROPOSAL_PREVIEW];

   this.router.navigate(route, {
     queryParams: { tab: PreviewPageTab.DETAILS }
   })?.then();
 }

  mapProposalToProposalTile() {
    this.proposalTile.seatNumber = this.proposal.proposalInformation.seatNumber;
    this.proposalTile.creationDate = new Date();
    this.proposalTile.riskDateBegin = this.proposal.proposalInformation.riskDateBegin;
    this.proposalTile.riskDateEnd = this.proposal.proposalInformation.riskDateEnd;
    this.proposalTile.proposalName = (this.proposal.proposalInformation.proposalName !== "") ? this.proposal.proposalInformation.proposalName : "Inserat";
    this.proposalTile.lineOfBusiness = this.proposal.lineOfBusiness;
    this.proposalTile.lineOfBusinessSubcategory = this.proposal.proposalInformation.lineOfBusinessSubcategory;
    this.proposalTile.deadline = this.proposal.proposalInformation.deadline;
    this.proposalTile.insuredAmount = this.proposal.proposalInformation.insuredAmount;
    this.proposalTile.insuredAmountCurrency = this.proposal.proposalInformation.insuredAmountCurrency;
    this.proposalTile.annualSalesAmount = this.proposal.proposalInformation.annualSalesAmount;
    this.proposalTile.privateProposal = this.proposal.proposalInformation.privateProposal;
    this.proposalTile.coverage = 0.0;
    if (this.proposal.proposalInformation.coveragePercentageRequested < 100)
      this.proposalTile.coverage = 1 - this.proposal.proposalInformation.coveragePercentageRequested/100;
    this.proposalTile.userId = this.activeUser.id;
    this.proposalTile.userType = this.activeUser.type;
    this.proposalTile.firstName = this.activeUser.firstName;
    this.proposalTile.lastName = this.activeUser.lastName;
    this.proposalTile.companyName = this.activeUser.company?.name;
    this.proposalTile.seatNumber = this.activeUser.company?.seatNumber;
  }

  openResetDialog() {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.disableClose = true;
    dialogConfig.autoFocus = true;
    dialogConfig.data = {
      title: this.translate.instant('proposalPreview.resetDialog.title'),
      body: this.translate.instant('proposalPreview.resetDialog.content'),
      optionCancel: this.translate.instant('proposalPreview.resetDialog.no'),
      optionConfirm: this.translate.instant('proposalPreview.resetDialog.yes'),
    };
    const dialogRef = this.dialog.open(DialogTwoOptionsComponent, dialogConfig);
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {

        //delete all already uploaded proposal files that are no longer needed
        this.proposal.proposalInformation.attachedFiles.forEach(attachedFile => {
          this.fileUploadService.deleteFileUpload(attachedFile.id).subscribe(() => {});
        })

        this.fileUploadService.clearAll();
        this.uploadComponents.forEach(uploadComponent => {
          uploadComponent.clear();
        });
        this.proposalStore.dispatch(ProposalDetailPageActions.resetPreview());
        this.proposal = new Proposal();
        for (let form of this.formComponents) {
          form.reset();
        }

      } else {
        this.scroll(this.idNames[this.maxScrolledPreview]);
      }
    });
  }

  protected readonly Roles = Roles;

  onRiskLocationCompleted(value: boolean): void {
    this.hasRiskLocationInput = value;
  }
}
